import { Button, DrawerCard, DrawerField, DrawerTitle } from "@mixitone/components";
import CalendarIcon from "@mixitone/icons/calendar.svg";
import LinkIcon from "@mixitone/icons/link.svg";
import { ApplicationView } from "@mixitone/mvc";
import { formatNumber } from "@mixitone/util";
import { RoutedLink } from "components/common/Link";
import { DrawerContextLink } from "components/common/DrawerContextLink";
import { RoutedDrawer } from "components/common/RoutedDrawer";
import dayjs from "dayjs";
import { injectModal } from "lib/injectParams";
import { SessionDrawerController } from "../SessionDrawerController";
import PlayerDot from "components/common/PlayerDot";
import { useModals } from "router";

interface Props {
  clubId: string;
  id: string;
}

const SessionDrawer = ({ clubId }: Props) => {
  const controller = SessionDrawerController.use();
  const modals = useModals();
  const { loading, night, newPlayers, totalGames, totalSets, incidentCount } = controller.state;

  const totalCollected = night.player_fee ? night.player_fee * (night.players?.length ?? 0) : 0;

  return (
    <RoutedDrawer spinner={loading}>
      <DrawerTitle
        icon={<CalendarIcon className="mr-2 w-[36px] stroke-gray-500" />}
        contextLink={<DrawerContextLink context="sessions" params={{ clubId }} />}
      >
        Session Details
      </DrawerTitle>
      {loading ? (
        <div className="flex animate-pulse flex-col gap-4">
          <DrawerCard>
            <DrawerField label="Date">&nbsp;</DrawerField>
            <hr />
            <DrawerField label="Total Players">&nbsp;</DrawerField>
            <hr />
            <DrawerField label="Game Sets">&nbsp;</DrawerField>
            <DrawerField label="Total Games">&nbsp;</DrawerField>
            <hr />
            <DrawerField label="Incidents">&nbsp;</DrawerField>
            <hr />
            <DrawerField label="New Players">
              <div className="h-4 w-32 rounded bg-gray-100">&nbsp;</div>
            </DrawerField>
          </DrawerCard>
        </div>
      ) : (
        <DrawerCard>
          <DrawerField label="Date">{dayjs(night.date).format("MMMM D, YYYY")}</DrawerField>
          <hr />
          <DrawerField label="Total Players">
            {night.players?.length ?? 0}
            {night.id && (
              <RoutedLink
                kind="plain"
                className="ml-auto flex items-center gap-1"
                to="/t/:token/players"
                params={{ token: night.id! }}
              >
                <LinkIcon className="h-4 w-4 stroke-current" />
                View players
              </RoutedLink>
            )}
          </DrawerField>
          <hr />
          <DrawerField label="Game Sets">{totalSets}</DrawerField>
          <DrawerField label="Total Games">
            {totalGames}
            {night.id && (
              <RoutedLink
                kind="plain"
                className="ml-auto flex items-center gap-1"
                to="/t/:token/games"
                params={{ token: night.id! }}
              >
                <LinkIcon className="h-4 w-4 stroke-current" />
                View games
              </RoutedLink>
            )}
          </DrawerField>
          {night.id && (
            <>
              <hr />
              <DrawerField label="Incidents">
                {incidentCount}
                <Button
                  kind="link"
                  type="button"
                  className="ml-auto inline-flex items-center gap-1"
                  onClick={() => modals.open("/t/:token/log", { params: { token: night.id! } })}
                >
                  <LinkIcon className="h-4 w-4 stroke-current" />
                  View or log incidents
                </Button>
              </DrawerField>
            </>
          )}
          {night.player_fee && (
            <>
              <hr />
              <DrawerField label="Player Fee">${formatNumber(night.player_fee)}</DrawerField>
              <DrawerField label="Collected">${formatNumber(totalCollected)}</DrawerField>
            </>
          )}
          {newPlayers.length > 0 && (
            <>
              <hr />
              <DrawerField label="New Players">
                <div className="flex flex-col gap-2">
                  {newPlayers.map((player) => (
                    <PlayerDot
                      key={player.id}
                      player={player}
                      inline
                      onClick={() =>
                        modals.open("/clubs/:clubId/players/:id/edit", {
                          params: { clubId, id: player.id! },
                        })
                      }
                    />
                  ))}
                </div>
              </DrawerField>
            </>
          )}
        </DrawerCard>
      )}
    </RoutedDrawer>
  );
};

export default injectModal(SessionDrawerController.scope(ApplicationView(SessionDrawer)));
